import {
    Box,
    Card,
    Chip,
    LinearProgress,
    Stack,
    Table,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import AppPagination from 'components/AppPagination';
import Scrollbar from 'components/ScrollBar';
import {
    BodyTableCell,
    HeadTableCell,
    StyledTableRow,
} from 'components/table/StyledTable';
import useBoolean from 'hooks/useBoolean';
import columnShape from 'page-sections/submitted-abstract/columnShape';
import HeadingArea from 'page-sections/submitted-abstract/heading-area';
import SearchArea from 'page-sections/submitted-abstract/search-area';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
    useAsyncDebounce,
    useGlobalFilter,
    usePagination,
    useRowSelect,
    useSortBy,
    useTable,
} from 'react-table';
import axiosInstance from 'utils/axios';
import downloadPreview from 'utils/downloadPreview';

const SubmittedAbstract = () => {
    const [value, setValue] = useState('all');
    const [dataAbstract, setDataAbstract] = useState([]);
    const [exportData, setExportData] = useState([]);
    const [pageNo, setPageNo] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const theme = useTheme();
    const loading = useBoolean();
    const columns = useMemo(() => columnShape, []);
    const [header, setHeader] = useState([]);
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        state,
        setGlobalFilter,
    } = useTable(
        { columns, data: dataAbstract },
        useGlobalFilter,
        useSortBy,
        usePagination,
        useRowSelect,
    );

    const downloadAbstract = async (data) => {
        loading.onTrue();

        await downloadPreview(data);

        loading.onFalse();
    };

    const getData = async (param, pageParam = pageNo, limitParam = pageSize) => {
        loading.onTrue();
        setExportData([]);
        try {
            const res = await axiosInstance.get(
                `/api/prv/operator-submissions`,
                {
                    params: {
                        type_id: param,
                        status: 'submitted',
                        page: pageParam,
                        limit: limitParam,
                    },
                },
            );
            const items = Array.isArray(res.data) ? res.data : res.data?.data || [];
            const meta = Array.isArray(res.data) ? null : res.data?.meta || null;

            setDataAbstract(items);
            if (meta) {
                setTotalPages(meta.totalPages || 0);
                setTotalItems(meta.total || items.length);
            } else {
                setTotalPages(1);
                setTotalItems(items.length);
            }

            for (let a = 0; a < items.length; a++) {
                setExportData((exportData) => [
                    ...exportData,
                    {
                        No:
                            items[a].number ||
                            String(a + 1).padStart(6, '0'),
                        Name:
                            items[a].user.fullName ||
                            items[a].user.full_name,
                        Email: items[a].user.email,
                        Judul: items[a].title,
                    },
                ]);
            }
        } catch (err) {
            console.log(err);
        } finally {
            loading.onFalse();
        }
    };

    const getHeader = async () => {
        await axiosInstance
            .get('api/prv/submission-types', {
                params: {
                    status: 'submitted',
                }
            })
            .then((res) => setHeader(res.data))
            .catch((err) => console.log(err));
    };

    const navigate = useNavigate();

    useEffect(() => {
        getData('all', 1, pageSize);
        getHeader();
    }, []);

    const handleChange = async (_, currentPageNo) => {
        setPageNo(currentPageNo);
        await getData(value, currentPageNo, pageSize);
    };

    const handleDetail = (data) => {
        navigate(`/dashboard/operator/submitted-abstract/${data.id}`);
    };

    const changeTab = (_, newValue) => {
        setValue(newValue);
        setPageNo(1);
        getData(newValue, 1, pageSize);
    };
    
    const [searchValue, setSearchValue] = useState(state.globalFilter);
    const handleSearch = useAsyncDebounce(
        (value) => setGlobalFilter(value || undefined),
        200,
    );

    return (
        <Box pt={2} pb={4}>
            <Card sx={{ py: 2 }}>
                <Box px={3}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 2 }}
                    >
                        <HeadingArea
                            value={value}
                            changeTab={changeTab}
                            header={header}
                        />
                        <Chip
                            label={`${totalItems} items`}
                            color="primary"
                            variant="outlined"
                        />
                    </Stack>
                    <SearchArea
                        value={searchValue}
                        onChange={handleSearch}
                        setValue={setSearchValue}
                        data={exportData}
                    />
                </Box>

                {loading.value && <LinearProgress sx={{ mx: 3 }} />}
                <Scrollbar autoHide={true}>
                    {page.length > 0 ? (
                        <Table
                            {...getTableProps()}
                            size="small"
                            sx={{ minWidth: 900 }}
                        >
                            <TableHead
                                sx={{
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                {headerGroups.map((headerGroup, index) => (
                                    <TableRow
                                        key={index}
                                        {...headerGroup.getHeaderGroupProps()}
                                    >
                                        {headerGroup.headers.map(
                                            (column, index) => (
                                                <HeadTableCell
                                                    key={index}
                                                    {...column.getHeaderProps(
                                                        column.getSortByToggleProps(),
                                                    )}
                                                    sx={{
                                                        paddingY: 1,
                                                        fontSize: 13,
                                                        fontWeight: 700,
                                                        borderBottom: 0,
                                                        width: column.width,
                                                        color: 'text.secondary',
                                                        minWidth:
                                                            column.minWidth,
                                                        maxWidth:
                                                            column.maxWidth,
                                                        '&:last-child': {
                                                            textAlign: 'center',
                                                        },
                                                    }}
                                                >
                                                    {column.render('Header')}
                                                </HeadTableCell>
                                            ),
                                        )}
                                    </TableRow>
                                ))}
                            </TableHead>
                            <TableBody {...getTableBodyProps()}>
                                {page.map((row, index) => {
                                    prepareRow(row);
                                    return (
                                        <StyledTableRow
                                            {...row.getRowProps()}
                                            key={index}
                                        >
                                            {row.cells.map((cell, cellIndex) => (
                                                <BodyTableCell
                                                    key={cellIndex}
                                                    {...cell.getCellProps()}
                                                    sx={{ paddingY: 1.5 }}
                                                >
                                                    {cell.render('Cell', {
                                                        handleDownload: downloadAbstract,
                                                        handleDetail,
                                                    })}
                                                </BodyTableCell>
                                            ))}
                                        </StyledTableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    ) : (
                        !loading.value && (
                            <Box px={3} py={8} textAlign="center">
                                <Typography variant="h6">No data</Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Try adjusting filters or search
                                </Typography>
                            </Box>
                        )
                    )}
                </Scrollbar>

                <Stack alignItems="center" marginY={4}>
                    <AppPagination
                        shape="rounded"
                        onChange={handleChange}
                        page={pageNo}
                        count={totalPages}
                    />
                </Stack>
            </Card>
        </Box>
    );
};

export default SubmittedAbstract;
